import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/authContext';
import { useUser } from '../hooks/useUser';
import { isCreditOnlyBilling } from '../lib/billingMode';
import LanguageSelector from './LanguageSelector';
import OfflineStatusBadge from './OfflineStatusBadge';
import { PWADownloadIcon } from './PWADownloadIcon';
import { ThemeToggle } from './ThemeToggle';
import HamburgerMenu from './HamburgerMenu';
import LandingDesktopNav from './LandingDesktopNav';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { buildAvatarUrl } from '../lib/avatarUtils';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const Header: React.FC = () => {
    const { isAuthenticated, logout } = useAuth();
    const { user } = useUser();
    const creditOnly = isCreditOnlyBilling(user);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const handleAccountSettings = () => {
        navigate('/account-settings');
    };

    const handleAccountSecurity = () => {
        navigate('/account-security');
    };

    const handleAdminImageModels = () => {
        navigate('/admin/image-models');
    };

    const isLandingPage = !isAuthenticated && location.pathname === '/';

    return (
        <header className="fixed top-0 left-0 right-0 z-50 w-full bg-background/90 backdrop-blur-md border-b border-border shadow-sm">
            <div className="container mx-auto flex items-center justify-between px-4 py-3 min-h-[64px] max-h-[72px]">
                <div
                    role="button"
                    tabIndex={0}
                    className="flex items-center gap-3 cursor-pointer transition-all duration-200 hover:scale-105 px-2 py-1 rounded-lg hover:bg-accent shrink-0"
                    onClick={() => navigate(isAuthenticated ? '/app' : '/')}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(isAuthenticated ? '/app' : '/'); }}
                >
                    <img
                        src="/minifil-header.png"
                        alt="Minifil Logo"
                        className="h-10 w-auto"
                    />
                    <span className="text-xl font-bold text-foreground hidden sm:block mansalva-regular">
                        {t('app.title')}
                    </span>
                </div>

                {isLandingPage ? (
                    <LandingDesktopNav />
                ) : (
                    <div className="flex-1 flex justify-center min-w-0">
                        <OfflineStatusBadge />
                    </div>
                )}

                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                    {/* PWA Download Icon - Always visible */}
                    <PWADownloadIcon size="sm" />

                    {isAuthenticated ? (
                        <div className="flex items-center gap-2 sm:gap-3">
                            {/* User name - Hidden on small screens, shown on larger screens */}
                            <span
                                className="text-muted-foreground hidden md:block font-medium text-sm cursor-pointer hover:text-foreground transition-colors truncate max-w-[120px]"
                                onClick={handleAccountSettings}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleAccountSettings();
                                    }
                                }}
                            >
                                {user?.name}
                            </span>

                            {/* Account Settings Dropdown - Always visible */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="rounded-full hover:bg-accent h-8 w-8">
                                        <Avatar className="h-8 w-8">
                                            {user?.avatarUrl ? (
                                                <AvatarImage
                                                    src={buildAvatarUrl(user.avatarUrl, user.avatarVersion) || user.avatarUrl}
                                                    alt={user?.name || 'User avatar'}
                                                />
                                            ) : (
                                                <AvatarFallback className="text-sm">{user?.name?.charAt(0)}</AvatarFallback>
                                            )}
                                        </Avatar>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    {user?.isAdmin === true && (
                                        <>
                                            <DropdownMenuItem onClick={handleAdminImageModels} className="cursor-pointer">
                                                <ShieldCheck className="mr-2 h-4 w-4" />
                                                {t('admin.imageModels.nav', 'Image models')}
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                        </>
                                    )}
                                    <DropdownMenuItem onClick={handleAccountSettings} className="cursor-pointer">
                                        <span className="mr-2">⚙️</span> {t('account.settings')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={handleAccountSecurity} className="cursor-pointer">
                                        <ShieldCheck className="mr-2 h-4 w-4" /> {t('account.security.nav')}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                                        <span className="mr-2">👋</span> {t('auth.logout')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    ) : (
                        <Button
                            variant="outline"
                            onClick={() => navigate('/login')}
                            className="border-border hover:bg-accent"
                        >
                            🔑 {t('auth.login')}
                        </Button>
                    )}

                    {/* Theme Toggle - Always visible */}
                    <ThemeToggle />

                    {/* Language Selector - Hidden on small screens, shown on md and larger */}
                    <div className="hidden md:block">
                        <LanguageSelector />
                    </div>

                    {isLandingPage && (
                        <div className="lg:hidden">
                            <HamburgerMenu />
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
